import React, { useEffect, useRef } from 'react';
import Gargantua from './Gargantua';
import './cosmos.css';

// Fond cosmique profond : nébuleuse en dérive + champ d'étoiles en rotation lente
// (sensation d'orbite), avec parallaxe au pointeur et au scroll. Au départ, les
// étoiles sont éparses ; en descendant la page, un réseau de « neurones » se tisse
// entre elles (constellation). La couleur du réseau est pilotée par l'angle de
// lecture du CV via l'événement `cv-neurons`. Tout s'arrête si prefers-reduced-motion.
const Starfield = () => {
  const canvasRef = useRef(null);
  const nebulaRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const nebula = nebulaRef.current;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Progression de scroll 0 → 1 sur toute la page.
    let scrollP = 0;
    const onScroll = () => {
      nebula?.style.setProperty('--scroll', String(window.scrollY));
      const max = document.documentElement.scrollHeight - window.innerHeight;
      scrollP = max > 0 ? Math.min(1, window.scrollY / max) : 0;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    if (!canvas || reduce) {
      return () => window.removeEventListener('scroll', onScroll);
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return () => window.removeEventListener('scroll', onScroll);
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, cx = 0, cy = 0, maxR = 0;
    let theta = 0;
    let pointerX = 0, pointerY = 0, targetX = 0, targetY = 0;
    let ease = 0; // lissage de scrollP → intensité du graphe

    // Couleur des « neurones » — pilotée par l'angle de lecture (voir CVInteractive).
    // Défaut : cyan froid. On lisse vers la cible pour une transition douce.
    let nr = 143, ng = 227, nb = 240, nEnergy = 0.5;
    let tr = 143, tg = 227, tb = 240, tEnergy = 0.5;
    const onNeurons = (e) => {
      const d = e.detail;
      if (!d) return;
      tr = d.r; tg = d.g; tb = d.b; tEnergy = d.energy;
    };
    window.addEventListener('cv-neurons', onNeurons);

    // Étoiles filantes occasionnelles (traînée diagonale lumineuse)
    let shooters = [];
    let shootTimer = 90;

    let stars = [];
    let nodes = [];      // sous-ensemble relié (perf : O(nodes²))
    let px = [], py = []; // positions courantes des nœuds

    const seed = () => {
      const count = Math.min(520, Math.floor((w * h) / 4000));
      const nodeCount = Math.min(120, Math.floor(count * 0.48));
      stars = Array.from({ length: count }, (_, i) => {
        const z = Math.random();               // profondeur 0 (loin) → 1 (proche)
        const r = z * 1.4 + 0.3;
        return {
          rad: Math.pow(Math.random(), 0.7) * maxR,
          ang: Math.random() * Math.PI * 2,
          z, r,
          a: z * 0.55 + 0.35,
          tw: Math.random() * 0.02 + 0.004,
          ph: Math.random() * Math.PI * 2,
          glow: r > 1.45 && Math.random() > 0.6,
          node: i < nodeCount,
        };
      });
      nodes = stars.filter((s) => s.node);
      px = new Array(nodes.length).fill(0);
      py = new Array(nodes.length).fill(0);
    };

    const resize = () => {
      w = canvas.clientWidth; h = canvas.clientHeight;
      cx = w / 2; cy = h / 2;
      maxR = Math.hypot(w, h) / 2 * 1.1;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const pos = (s) => {
      const a = s.ang + theta * (0.35 + s.z * 0.9);      // les proches tournent plus vite
      return [
        cx + Math.cos(a) * s.rad + pointerX * (s.z * 22 + 4),
        cy + Math.sin(a) * s.rad + pointerY * (s.z * 22 + 4),
      ];
    };

    // Halos pré-rendus (sprites) : remplacent le shadowBlur par étoile — très coûteux
    // en canvas 2D (flou offscreen à chaque tracé) et source de la montée mémoire GPU.
    const GLOW = 48;
    const paintGlow = (cv, r, g, b) => {
      const gc = cv.getContext('2d');
      gc.clearRect(0, 0, GLOW, GLOW);
      const m = GLOW / 2;
      const grad = gc.createRadialGradient(m, m, 0, m, m, m);
      grad.addColorStop(0, `rgba(${r},${g},${b},0.9)`);
      grad.addColorStop(0.4, `rgba(${r},${g},${b},0.35)`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      gc.fillStyle = grad; gc.fillRect(0, 0, GLOW, GLOW);
    };
    const mkGlow = (r, g, b) => { const cv = document.createElement('canvas'); cv.width = cv.height = GLOW; paintGlow(cv, r, g, b); return cv; };
    const glowStar = mkGlow(143, 227, 240);   // halo cyan des étoiles brillantes (ex shadowColor #8fe3f0)
    const glowShoot = mkGlow(207, 232, 255);  // halo glacé des étoiles filantes (ex #cfe8ff)
    const glowNode = mkGlow(nr, ng, nb);       // halo « neurone » — re-tinté quand la couleur bouge
    let glowKey = -1;

    let raf = 0, last = 0;
    const STEP = 1000 / 30;                              // fond plafonné à ~30 fps (2× moins de frames)
    const draw = (now = 0) => {
      raf = requestAnimationFrame(draw);
      const elapsed = now - last;
      if (elapsed < STEP) return;                        // throttle : on saute les frames trop rapprochées
      const dt = Math.min(elapsed, 100) / (1000 / 60);   // mouvement en « frames 60 fps » → vitesse préservée
      last = now;
      theta += 0.0002 * dt;
      pointerX += (targetX - pointerX) * 0.05 * dt;
      pointerY += (targetY - pointerY) * 0.05 * dt;
      ease += (scrollP - ease) * 0.06 * dt;              // lissage
      // lissage de la couleur des neurones vers la cible
      nr += (tr - nr) * 0.08 * dt; ng += (tg - ng) * 0.08 * dt; nb += (tb - nb) * 0.08 * dt;
      nEnergy += (tEnergy - nEnergy) * 0.08 * dt;
      const R = Math.round(nr), G = Math.round(ng), B = Math.round(nb);
      const gk = ((R >> 3) << 10) | ((G >> 3) << 5) | (B >> 3);   // couleur quantifiée
      if (gk !== glowKey) { paintGlow(glowNode, R, G, B); glowKey = gk; } // re-tinte le halo seulement quand la couleur change
      ctx.clearRect(0, 0, w, h);

      // 1) Liens de constellation : au départ AUCUN lien (étoiles éparses).
      //    Le réseau se tisse à mesure qu'on descend (ease : 0 en haut → 1 en bas).
      if (ease > 0.02) {
        const D = Math.min(w, h) * (0.28 + nEnergy * 0.08); // portée d'un lien
        const D2 = D * D;
        for (let i = 0; i < nodes.length; i++) {
          const [x, y] = pos(nodes[i]); px[i] = x; py[i] = y;
        }
        ctx.lineWidth = 1;
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dx = px[i] - px[j], dy = py[i] - py[j];
            const d2 = dx * dx + dy * dy;
            if (d2 < D2) {
              const closeness = 1 - Math.sqrt(d2) / D;
              const alpha = closeness * (0.5 + nEnergy * 0.4) * ease;
              if (alpha > 0.01) {
                ctx.strokeStyle = `rgba(${R},${G},${B},${alpha})`;
                ctx.beginPath();
                ctx.moveTo(px[i], py[i]);
                ctx.lineTo(px[j], py[j]);
                ctx.stroke();
              }
            }
          }
        }
      }

      // 2) Étoiles — les nœuds prennent la couleur des neurones quand le réseau se forme
      const neuronCol = `rgb(${R},${G},${B})`;
      for (const s of stars) {
        const [x, y] = pos(s);
        const alpha = Math.max(0, Math.min(1, s.a + Math.sin(theta * 400 * s.tw + s.ph) * 0.28));
        ctx.globalAlpha = alpha;
        // au départ, les nœuds sont de simples étoiles ; ils se colorent au scroll
        const rad = s.node ? s.r + ease * (0.7 + nEnergy) : s.r;
        if (s.glow || (s.node && ease > 0.15)) {           // halo via sprite pré-rendu (ex-shadowBlur)
          const sp = s.node && ease > 0.15 ? glowNode : glowStar;
          const gs = (rad + 2 + nEnergy * 3) * 4;
          ctx.globalAlpha = alpha * 0.7;
          ctx.drawImage(sp, x - gs / 2, y - gs / 2, gs, gs);
          ctx.globalAlpha = alpha;
        }
        ctx.fillStyle = s.node && ease > 0.08 ? neuronCol : (s.r > 1.2 ? '#cfe8ff' : '#eef2fb');
        ctx.beginPath();
        ctx.arc(x, y, rad, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1; ctx.shadowBlur = 0;

      // 3) Étoiles filantes
      shootTimer -= dt;
      if (shootTimer <= 0) {
        const fromLeft = Math.random() > 0.5;
        const sx = fromLeft ? Math.random() * w * 0.45 : w * 0.55 + Math.random() * w * 0.45;
        const speed = 7 + Math.random() * 4;
        const ang = (fromLeft ? 0.5 : Math.PI - 0.5) + (Math.random() - 0.5) * 0.25;
        shooters.push({ x: sx, y: -20, vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed * 0.7 + 2, life: 64, max: 64 });
        shootTimer = 130 + Math.floor(Math.random() * 240);
      }
      ctx.lineCap = 'round';
      for (let i = shooters.length - 1; i >= 0; i--) {
        const s = shooters[i];
        s.x += s.vx * dt; s.y += s.vy * dt; s.life -= dt;
        const t = Math.max(0, s.life / s.max);
        const inv = 1 / (Math.hypot(s.vx, s.vy) || 1);
        const tx = s.x - s.vx * inv * 95, ty = s.y - s.vy * inv * 95;
        const grad = ctx.createLinearGradient(s.x, s.y, tx, ty);
        grad.addColorStop(0, `rgba(255,255,255,${0.9 * t})`);
        grad.addColorStop(1, 'rgba(180,220,255,0)');
        ctx.strokeStyle = grad; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(tx, ty); ctx.stroke();
        const hs = 16;
        ctx.globalAlpha = t * 0.8; ctx.drawImage(glowShoot, s.x - hs / 2, s.y - hs / 2, hs, hs);
        ctx.globalAlpha = t; ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(s.x, s.y, 1.6, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
        if (s.life <= 0 || s.y > h + 40 || s.x < -40 || s.x > w + 40) shooters.splice(i, 1);
      }
    };

    const onPointerMove = (e) => {
      targetX = (e.clientX / window.innerWidth - 0.5);
      targetY = (e.clientY / window.innerHeight - 0.5);
      nebula?.style.setProperty('--px', `${targetX * -14}px`);
    };
    // Pause totale hors champ : onglet caché OU fenêtre non focalisée (l'appli au
    // premier plan d'une autre fenêtre → le fond ne doit plus consommer CPU/GPU).
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    const start = () => { if (!raf && !document.hidden && document.hasFocus()) { last = 0; raf = requestAnimationFrame(draw); } };
    const onVisibility = () => (document.hidden ? stop() : start());

    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', stop);
    window.addEventListener('focus', start);
    start();

    return () => {
      stop();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('cv-neurons', onNeurons);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', stop);
      window.removeEventListener('focus', start);
    };
  }, []);

  return (
    <div className="cosmos" aria-hidden="true">
      <div className="layer base"></div>
      <div className="layer nebula" ref={nebulaRef}></div>
      <canvas className="layer stars" ref={canvasRef}></canvas>
      <div className="gargantua-anchor">
        <Gargantua />
      </div>
      <div className="layer vignette"></div>
    </div>
  );
};

export default Starfield;
